import type { CatalogProduct, ProductCatalogResponse } from '@techgitter/contracts';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CACHE_KEY = 'products_cache_v1';
const TTL_MS = 86_400_000; // 24 hours
const SCHEMA_VERSION = 4;
const API_URL = '/api/products';
const CACHE_CLEAR_URL = '/api/products/cache/clear';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CacheEnvelope {
  cachedAt: number;       // Unix ms
  ttlMs: number;
  catalogVersion: string;
  schemaVersion: number;
  items: CatalogProduct[];
}

export interface GetProductsOptions {
  /** Bypass cache and fetch from network */
  forceRefresh?: boolean;
  /** If true, return stale cache even when a refresh is attempted */
  allowStale?: boolean;
}

export interface GetProductsResult {
  items: CatalogProduct[];
  catalogVersion: string;
  isStale?: boolean;
  warning?: string;
}

export interface RefreshResult {
  success: boolean;
  items?: CatalogProduct[];
  error?: string;
}

export interface InvalidateResult {
  success: boolean;
  serverCleared: boolean;
  error?: string;
}

// ---------------------------------------------------------------------------
// Module-level state (in-memory tier)
// ---------------------------------------------------------------------------

let memoryCache: CacheEnvelope | null = null;
let inflightRequest: Promise<GetProductsResult> | null = null;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function isFresh(envelope: CacheEnvelope): boolean {
  return Date.now() - envelope.cachedAt < envelope.ttlMs;
}

function readLocalStorage(): CacheEnvelope | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed: CacheEnvelope = JSON.parse(raw);
    if (parsed.schemaVersion !== SCHEMA_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLocalStorage(envelope: CacheEnvelope): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(envelope));
  } catch {
    // localStorage full or unavailable – silent fallback to memory-only
  }
}

/** Accept both canonical "items" and legacy "products" field. */
function normalizeResponse(data: Record<string, unknown>): ProductCatalogResponse {
  const items = (data.items ?? data.products) as CatalogProduct[] | undefined;
  if (!Array.isArray(items)) {
    throw new Error('Invalid product catalog response: no items array');
  }
  return {
    items,
    catalogVersion: (data.catalogVersion as string) ?? 'unknown',
  };
}

function buildEnvelope(res: ProductCatalogResponse): CacheEnvelope {
  return {
    cachedAt: Date.now(),
    ttlMs: TTL_MS,
    catalogVersion: res.catalogVersion,
    schemaVersion: SCHEMA_VERSION,
    items: res.items,
  };
}

function storeEnvelope(envelope: CacheEnvelope): void {
  memoryCache = envelope;
  writeLocalStorage(envelope);
}

/** Resolve the best available cached envelope (memory → localStorage). */
function resolveCache(): CacheEnvelope | null {
  if (memoryCache) return memoryCache;
  const stored = readLocalStorage();
  if (stored) {
    memoryCache = stored; // promote to memory
  }
  return stored;
}

async function fetchFromNetwork(bypassHttpCache = false): Promise<ProductCatalogResponse> {
  const response = await fetch(API_URL, bypassHttpCache ? { cache: 'no-store' } : {});
  if (!response.ok) {
    throw new Error(`Product catalog fetch failed: ${response.status}`);
  }
  const data = await response.json();
  return normalizeResponse(data);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Fetch the product catalog using two-tier cache with stale fallback.
 *
 * Policy:
 * - Fresh cache → return immediately (no network)
 * - Expired cache → try network; on failure return stale with warning
 * - No cache → fetch from network; throw if network also fails
 *
 * Concurrent calls share a single in-flight request.
 */
export async function getProducts(
  options?: GetProductsOptions,
): Promise<GetProductsResult> {
  const { forceRefresh = false, allowStale = true } = options ?? {};

  // --- Fast path: fresh in-memory / localStorage cache ---
  if (!forceRefresh) {
    const cached = resolveCache();
    if (cached && isFresh(cached)) {
      return {
        items: cached.items,
        catalogVersion: cached.catalogVersion,
      };
    }
  }

  // --- Deduplicate concurrent network requests ---
  if (inflightRequest && !forceRefresh) {
    return inflightRequest;
  }

  const networkPromise = (async (): Promise<GetProductsResult> => {
    try {
      const res = await fetchFromNetwork(forceRefresh);
      const envelope = buildEnvelope(res);
      storeEnvelope(envelope);
      return { items: res.items, catalogVersion: res.catalogVersion };
    } catch (err: any) {
      // Stale fallback
      if (allowStale) {
        const stale = resolveCache();
        if (stale) {
          return {
            items: stale.items,
            catalogVersion: stale.catalogVersion,
            isStale: true,
            warning: `Using cached catalog (network error: ${err.message})`,
          };
        }
      }
      throw err;
    } finally {
      inflightRequest = null;
    }
  })();

  inflightRequest = networkPromise;
  return networkPromise;
}

/**
 * Force-refresh the cache. Designed for a Settings "Refresh" button.
 * Clears the server-side cache first to ensure the API returns fresh data.
 * On failure the existing cache is preserved.
 */
export async function refreshProductsCache(): Promise<RefreshResult> {
  try {
    // Clear server-side cache before fetching so the API returns fresh data
    try {
      await fetch(CACHE_CLEAR_URL, { method: 'POST' });
    } catch {
      // Server cache clear is best-effort — continue with fetch
    }

    const res = await fetchFromNetwork(true);
    const envelope = buildEnvelope(res);
    storeEnvelope(envelope);
    return { success: true, items: res.items };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

/**
 * Clear both client-side and server-side caches.
 */
export async function invalidateProductsCache(): Promise<InvalidateResult> {
  // Clear client-side caches
  memoryCache = null;
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }

  // Clear server-side cache
  try {
    const res = await fetch(CACHE_CLEAR_URL, { method: 'POST' });
    const data = await res.json();
    return { success: true, serverCleared: data.cleared };
  } catch (error: any) {
    // Client cache is still cleared even if server call fails
    return { success: true, serverCleared: false, error: error.message };
  }
}

// ---------------------------------------------------------------------------
// Cache metadata (used by DebugPanel)
// ---------------------------------------------------------------------------

export function getCacheMetadata(): {
  cachedAt: number;
  ttlMs: number;
  catalogVersion: string;
  recordCount: number;
} | null {
  const envelope = resolveCache();
  if (!envelope) return null;
  return {
    cachedAt: envelope.cachedAt,
    ttlMs: envelope.ttlMs,
    catalogVersion: envelope.catalogVersion,
    recordCount: envelope.items.length,
  };
}

// ---------------------------------------------------------------------------
// Test helpers (exported for unit tests only)
// ---------------------------------------------------------------------------

/** @internal Reset all module state for tests (memory + localStorage). */
export function _resetForTesting(): void {
  memoryCache = null;
  inflightRequest = null;
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}

/** @internal Clear only the in-memory cache (simulates page reload). */
export function _clearMemoryForTesting(): void {
  memoryCache = null;
  inflightRequest = null;
}
